/**
 * SessionMarkersPlugin - Draw vertical lines with labels for market session times
 * Marks: Globex Open, London Open/Close, RTH Open/Close
 */

class SessionMarkersPlugin {
  constructor(options = {}) {
    this._options = {
      showGlobexOpen: true,
      showLondonOpen: true,
      showLondonClose: true,
      showRTHOpen: true,
      showRTHClose: true,
      timezone: 'America/New_York',
      colors: {
        globexOpen: 'rgba(147, 51, 234, 0.6)',   // Purple
        londonOpen: 'rgba(59, 130, 246, 0.6)',   // Blue
        londonClose: 'rgba(59, 130, 246, 0.4)', // Blue (dimmer)
        rthOpen: 'rgba(34, 197, 94, 0.6)',       // Green
        rthClose: 'rgba(34, 197, 94, 0.4)',     // Green (dimmer)
      },
      lineWidth: 1,
      lineStyle: 2, // 0=solid, 1=dotted, 2=dashed
      labelTextColor: '#e0e0e0',
      labelBackgroundColor: 'rgba(26, 31, 46, 0.9)',
      fontSize: 10,
      ...options
    };

    this._chart = null;
    this._series = null;
    this._paneViews = [];
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
    this.updateAllViews();
    if (this._requestUpdate) {
      this._requestUpdate();
    }
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return this._paneViews;
  }

  updateAllViews() {
    this._paneViews = [new SessionMarkersPaneView(this._options, this._chart)];
  }

  updateOptions(options) {
    this._options = { ...this._options, ...options };
    this.updateAllViews();
    if (this._requestUpdate) {
      this._requestUpdate();
    }
  }
}

class SessionMarkersPaneView {
  constructor(options, chart) {
    this._options = options;
    this._chart = chart;
  }

  zOrder() {
    return 'bottom';
  }

  renderer() {
    return new SessionMarkersRenderer(this._options, this._chart);
  }
}

class SessionMarkersRenderer {
  constructor(options, chart) {
    this._options = options;
    this._chart = chart;
  }

  draw(target) {
    if (!this._chart) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const timeScale = this._chart.timeScale();

      const visibleRange = timeScale.getVisibleRange();
      if (!visibleRange) return;

      // Define session times in ET (hours:minutes)
      const sessionTimes = [];

      if (this._options.showGlobexOpen) {
        sessionTimes.push({
          hour: 18,
          minute: 0,
          label: 'Globex',
          color: this._options.colors.globexOpen
        });
      }
      if (this._options.showLondonOpen) {
        sessionTimes.push({
          hour: 8,
          minute: 0,
          label: 'London Open',
          color: this._options.colors.londonOpen,
          timezone: 'Europe/London'
        });
      }
      if (this._options.showLondonClose) {
        sessionTimes.push({
          hour: 16,
          minute: 30,
          label: 'London Close',
          color: this._options.colors.londonClose,
          timezone: 'Europe/London'
        });
      }
      if (this._options.showRTHOpen) {
        sessionTimes.push({
          hour: 9,
          minute: 30,
          label: 'RTH Open',
          color: this._options.colors.rthOpen
        });
      }
      if (this._options.showRTHClose) {
        sessionTimes.push({
          hour: 16,
          minute: 0,
          label: 'RTH Close',
          color: this._options.colors.rthClose
        });
      }

      // Find all session markers in visible range
      const markers = this._findSessionMarkers(
        visibleRange.from,
        visibleRange.to,
        sessionTimes
      );

      // Draw each marker
      markers.forEach(marker => {
        const x = timeScale.timeToCoordinate(marker.time);
        if (x === null) return;

        const bitmapX = Math.round(x * scope.horizontalPixelRatio);

        // Draw vertical line
        ctx.save();
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = this._options.lineWidth * scope.horizontalPixelRatio;

        // Set line style
        if (this._options.lineStyle === 1) {
          ctx.setLineDash([2, 2]);
        } else if (this._options.lineStyle === 2) {
          ctx.setLineDash([5, 5]);
        } else if (this._options.lineStyle === 3) {
          ctx.setLineDash([10, 5]);
        }

        ctx.beginPath();
        ctx.moveTo(bitmapX, 0);
        ctx.lineTo(bitmapX, scope.bitmapSize.height);
        ctx.stroke();
        ctx.restore();

        // Draw label at the top
        ctx.save();
        const fontSize = this._options.fontSize * scope.verticalPixelRatio;
        ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        const text = marker.label;
        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;
        const textHeight = fontSize * 1.2;
        const padding = 3 * scope.horizontalPixelRatio;

        // Position label to the right of the line
        const labelX = bitmapX + (3 * scope.horizontalPixelRatio);
        const labelY = 5 * scope.verticalPixelRatio;

        // Draw label background
        ctx.fillStyle = this._options.labelBackgroundColor;
        ctx.fillRect(
          labelX - padding,
          labelY - padding,
          textWidth + (padding * 2),
          textHeight + (padding * 2)
        );

        // Draw label border
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.strokeRect(
          labelX - padding,
          labelY - padding,
          textWidth + (padding * 2),
          textHeight + (padding * 2)
        );

        // Draw label text
        ctx.fillStyle = marker.color;
        ctx.fillText(text, labelX, labelY);
        ctx.restore();
      });
    });
  }

  _findSessionMarkers(fromTime, toTime, sessionTimes) {
    const markers = [];

    const startDate = new Date(fromTime * 1000);
    const endDate = new Date(toTime * 1000);

    // Start one day before to catch overnight sessions
    const currentTime = new Date(startDate.getTime() - 86400000);

    // Pre-create formatters outside loop for performance
    const etDateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const londonDateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const etHourFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      hour12: false,
    });
    const londonHourFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/London',
      hour: '2-digit',
      hour12: false,
    });

    while (currentTime <= new Date(endDate.getTime() + 86400000)) {
      sessionTimes.forEach(session => {
        const isLondon = session.timezone === 'Europe/London';

        // Get current date in the appropriate timezone
        const dateStr = (isLondon ? londonDateFormatter : etDateFormatter).format(currentTime);
        const [month, day, year] = dateStr.split('/');

        const yr = parseInt(year);
        const mo = parseInt(month);
        const dy = parseInt(day);

        // Compute UTC offset by comparing noon UTC with local hour
        // This avoids relying on locale-dependent timezone name strings
        const ref = new Date(Date.UTC(yr, mo - 1, dy, 12, 0, 0));
        let offset;
        if (isLondon) {
          const localHour = parseInt(londonHourFormatter.format(ref));
          const offsetHours = localHour - 12;
          offset = offsetHours > 0 ? `+${String(offsetHours).padStart(2, '0')}:00` : '+00:00';
        } else {
          const localHour = parseInt(etHourFormatter.format(ref));
          const offsetHours = localHour - 12;
          offset = `-${String(Math.abs(offsetHours)).padStart(2, '0')}:00`;
        }

        const isoString = `${yr}-${String(mo).padStart(2, '0')}-${String(dy).padStart(2, '0')}T${String(session.hour).padStart(2, '0')}:${String(session.minute).padStart(2, '0')}:00${offset}`;

        const markerDate = new Date(isoString);
        const timestamp = Math.floor(markerDate.getTime() / 1000);

        // Only add if within visible range and not on weekends
        const dayOfWeek = markerDate.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        // Skip weekends for RTH and London, but Globex opens Sunday 6 PM
        const isGlobexSundayOpen = session.label === 'Globex' && dayOfWeek === 0;
        const skipWeekend = isWeekend && !isGlobexSundayOpen;

        if (timestamp >= fromTime && timestamp <= toTime && !skipWeekend) {
          markers.push({
            time: timestamp,
            label: session.label,
            color: session.color
          });
        }
      });

      currentTime.setDate(currentTime.getDate() + 1);
    }

    return markers;
  }
}

export default SessionMarkersPlugin;
